import { ArrowBack, Block, DeleteSweep, Refresh } from '@mui/icons-material';
import {
    Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
    DialogContentText, DialogTitle, IconButton, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Tooltip, Typography
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import util from '../util';

function formatDate(isoStr) {
    if (!isoStr) return 'N/A';
    try {
        return new Date(isoStr).toLocaleString();
    } catch {
        return isoStr;
    }
}

function formatRemaining(until, now) {
    if (!until) return 'N/A';
    const ms = new Date(until).getTime() - now;
    if (ms <= 0) return 'expired';

    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
}

const reasonLabels = {
    login: 'Web login',
    'step-up': 'Password re-entry',
    'password-change': 'Password change',
    'git-auth': 'Git token',
    'git-auth-malformed': 'Malformed git auth',
    'mcp-auth': 'MCP key',
    'ftp-auth': 'FTP login',
    'ftp-nontext': 'Corrupt FTP traffic',
    'ftp-line-too-long': 'FTP line flood',
    'ftp-tls-garbage': 'TLS bytes on plain FTP',
    'http-bad-request': 'Malformed HTTP',
    'http-malformed': 'Corrupt protocol request'
};

const reasonLabel = (reason) => reasonLabels[reason] || reason || 'Unknown';

const AdminBansPage = () => {
    const navigate = useNavigate();
    const [bans, setBans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [unbanIp, setUnbanIp] = useState(null);
    const [unbanning, setUnbanning] = useState(false);
    const [now, setNow] = useState(Date.now());

    const fetchBans = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await util.getRequest('/api/admin/bans');
            if (!res || res.code) {
                throw new Error('Failed to fetch banned IPs');
            }
            setBans(Array.isArray(res.data) ? res.data : []);
        } catch (e) {
            setError(e.message || 'Failed to fetch banned IPs');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        const user = JSON.parse(localStorage.getItem('user') || 'false');
        if (!user || user.role !== 'admin') {
            navigate('/contentManager');
            return;
        }
        fetchBans();
    }, [fetchBans, navigate]);

    useEffect(() => {
        const timer = setInterval(() => setNow(Date.now()), 10000);
        return () => clearInterval(timer);
    }, []);

    const handleUnban = async () => {
        if (!unbanIp) return;
        setUnbanning(true);
        try {
            const res = await util.deleteRequest(`/api/admin/bans/${encodeURIComponent(unbanIp)}`);
            if (!res || res.code) return;
            setBans((current) => current.filter((b) => b.ip !== unbanIp));
            setUnbanIp(null);
        } finally {
            setUnbanning(false);
        }
    };

    return (
        <Box sx={{ mt: 2, p: 2, backgroundColor: '#151515', borderRadius: 1, minHeight: 'calc(100vh - 110px)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <IconButton onClick={() => navigate('/admin')}>
                    <ArrowBack />
                </IconButton>
                <Typography variant="h5" sx={{ ml: 1 }}>Blocked IPs</Typography>
                <Chip label={`${bans.length} active`} size="small" sx={{ ml: 2 }} />
                <Box sx={{ ml: 'auto' }}>
                    <Tooltip title="Refresh">
                        <span>
                            <IconButton onClick={fetchBans} disabled={loading}>
                                <Refresh />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
            </Box>

            <Typography variant="body2" sx={{ opacity: 0.7, mb: 2 }}>
                IPs are blocked automatically after repeated failed logins or corrupt protocol traffic
                (web, Git, MCP and FTP). Bans are shared across all protocols and survive restarts.
            </Typography>

            {loading ? (
                <Box sx={{ mt: '25vh', textAlign: 'center' }}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Typography color="error">{error}</Typography>
            ) : (
                <TableContainer component={Paper} sx={{ backgroundColor: '#1d1d1d' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>IP</TableCell>
                                <TableCell>Reason</TableCell>
                                <TableCell align="right">Strikes</TableCell>
                                <TableCell align="right">Repeats</TableCell>
                                <TableCell>Blocked until</TableCell>
                                <TableCell align="right">Remaining</TableCell>
                                <TableCell align="right">Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {bans.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                                        <Block sx={{ display: 'block', mx: 'auto', mb: 1, opacity: 0.4 }} />
                                        <Typography sx={{ opacity: 0.7 }}>No active bans.</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                            {bans.map((ban) => (
                                <TableRow key={ban.ip} sx={{ '&:hover': { backgroundColor: '#333' } }}>
                                    <TableCell>
                                        <Typography component="span" fontFamily="monospace">
                                            {ban.ip}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Tooltip title={ban.reason || ''}>
                                            <Chip label={reasonLabel(ban.reason)} size="small" color="warning" variant="outlined" />
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell align="right">{ban.strikes}</TableCell>
                                    <TableCell align="right">{ban.repeatCount}</TableCell>
                                    <TableCell>{formatDate(ban.bannedUntil)}</TableCell>
                                    <TableCell align="right">{formatRemaining(ban.bannedUntil, now)}</TableCell>
                                    <TableCell align="right">
                                        <Button
                                            size="small"
                                            color="success"
                                            startIcon={<DeleteSweep />}
                                            onClick={() => setUnbanIp(ban.ip)}
                                        >
                                            Unban
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Dialog open={Boolean(unbanIp)} onClose={() => setUnbanIp(null)}>
                <DialogTitle>Unban IP</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Remove the ban for {unbanIp}? It will be allowed to connect again immediately.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUnbanIp(null)} disabled={unbanning}>Cancel</Button>
                    <Button color="success" variant="contained" onClick={handleUnban} disabled={unbanning}>
                        Unban
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AdminBansPage;
